import { nanoid } from 'nanoid';
import { ReactComponent as StatusIcon } from '../assets/icons/status-icon.svg';
import { ReactComponent as TypeIcon } from '../assets/icons/survey-type-icon.svg';
import { ReactComponent as ReviewersIcon } from '../assets/icons/reviewer-icon.svg';
import { ReactComponent as CreatorIcon } from '../assets/icons/creator-icon.svg';
import { ReactComponent as SubjectIcon } from '../assets/icons/subject-icon.svg';
import { ATTRIBUTES as ATTRIBUTE_CONSTANTS } from '../constants/attributes';
import {
  SURVEY_VIEW_OPTIONS,
  SURVEY_TYPES,
  CREATE_SURVEY_FIELD_NAMES,
  SURVEY_TYPES_DIALOG_MAP,
  SURVEY_TYPES_MAP,
  AUTO_REMINDER_OPTION_IDS,
  getCreateSurveyAutoReminderStartDate,
  pruneAutoReminders,
  QUESTIONNAIRE_QUESTION_DEFAULT_OPTIONS_COUNT,
  QUESTIONNAIRE_QUESTION_TYPES,
  QUESTIONNAIRE_QUESTION_OPTIONS,
  EMPTY_QUESTIONNAIRE,
  QUESTIONNAIRE_NAME_VALIDATORS,
  QUESTIONNAIRE_DESCRIPTION_VALIDATORS,
  QUESTIONNAIRE_QUESTION_MIN_OPTIONS,
  QUESTIONNAIRE_QUESTION_MAX_OPTIONS,
  TITLE_FIELD,
} from '../constants/survey';
import {
  SURVEY_STATUSES,
  SURVEY_STATUS_TYPES,
  SURVEY_COMPLETION_STATUSES,
} from '../constants/statuses';
import { PARAMS } from '../constants/pages';
import {
  isObject,
  isObjectEmpty,
  isArrayEmpty,
  trimString,
  getEmployees,
  getItemById,
  getObjectToNumberArray,
  isItemInList,
  isValueInList,
} from './helpers';
import {
  formatDate,
  addHoursToCurrentStartHour,
  addDaysFromSpecificHour,
  isDateInFuture,
} from './dateUtils';
import { prepareFilterableUsers } from './uiUtils';
import {
  isEmpty,
  validateLength,
  forbiddenCharacters,
  isRichTextEditorEmpty,
} from './validation';

export const prepareSurveyTypes = (translations, isDialogMap = false) => {
  const surveyTypes = isDialogMap ? SURVEY_TYPES_DIALOG_MAP : SURVEY_TYPES_MAP;

  return Object.keys(surveyTypes).map(type => ({
    ...surveyTypes[type],
    ...translations.surveyTypes[type.toLowerCase()],
  }));
};

export const prepareSurveyStatuses = labels =>
  SURVEY_STATUSES.map(asu => ({
    ...asu,
    name: labels[asu.key],
  }));

export const prepareSurveyReviewerStatuses = (
  labels,
  hasAll = false,
  shouldRemoveStarted = false
) => {
  const statuses = SURVEY_COMPLETION_STATUSES.reduce((acc, status) => {
    if (shouldRemoveStarted && status.key === 'started') return acc;

    return [
      ...acc,
      {
        ...status,
        name: labels[status.key],
      },
    ];
  }, []);

  if (hasAll) {
    return [{ id: 0, name: labels.all }, ...statuses];
  }

  return statuses;
};

export const prepareSurveyTitle = (
  defaultTitle,
  attributes = [],
  previousAttributes = [],
  currentTitle = ''
) => {
  const [initialTitle] = defaultTitle.split(' -');
  const [currentTitlePartOne] = currentTitle.split(' -');

  const attributesText =
    attributes.length > 0
      ? attributes.reduce((acc, attribute) => {
          if (acc.length >= 30) {
            return acc;
          }
          return `${acc} ${attribute.code}`;
        }, ' -')
      : '';
  const previousAttributesText =
    previousAttributes.length > 0
      ? previousAttributes.reduce((acc, attribute) => {
          if (acc.length >= 30) {
            return acc;
          }
          return `${acc} ${attribute.code}`;
        }, ' -')
      : '';

  if (
    (currentTitlePartOne && trimString(currentTitlePartOne) !== initialTitle) ||
    (currentTitle &&
      trimString(currentTitle) !== `${initialTitle}${previousAttributesText}`)
  )
    return currentTitle;

  return `${initialTitle}${attributesText}`;
};

export const prepareSurveyViewOptions = labels =>
  SURVEY_VIEW_OPTIONS.map(view => ({
    ...view,
    name: labels[view.key],
  }));

export const prepareSurveysPageFilters = (
  translations,
  users,
  canViewAllSurveys
) => {
  const { TYPE, CREATOR, STATUS, REVIEWERS, CREATED_FOR } = PARAMS;
  const types = prepareSurveyTypes(translations);
  const allUsers = prepareFilterableUsers(users);
  const allEmployees = getEmployees(allUsers);
  const defaultOption = { id: null, name: translations.all };
  const translatedStatuses = prepareSurveyStatuses(translations.statuses);

  return [
    {
      id: STATUS,
      name: translations.filters.labels[STATUS],
      icon: StatusIcon,
      isSingleSelect: true,
      items: [defaultOption].concat(translatedStatuses),
    },
    {
      id: TYPE,
      name: translations.filters.labels[TYPE],
      icon: TypeIcon,
      isSingleSelect: true,
      items: [{ id: null, name: translations.all }, ...types],
    },
    {
      id: REVIEWERS,
      name: translations.filters.labels[REVIEWERS],
      icon: ReviewersIcon,
      items: allUsers,
    },
    ...(canViewAllSurveys
      ? [
          {
            id: CREATOR,
            name: translations.filters.labels[CREATOR],
            icon: CreatorIcon,
            items: allEmployees,
          },
        ]
      : []),
    {
      id: CREATED_FOR,
      name: translations.filters.labels.subject,
      icon: SubjectIcon,
      items: allEmployees,
    },
  ];
};

export const prepareSurveysHeaderActions = labels => [
  {
    id: 'actions-column',
    title: labels.actions,
    rowKey: 'actions',
    align: 'right',
    isHeaderAction: true,
    rowCellClass: 'actions-dots-menu',
    minWidth: 60,
    maxWidth: 80,
  },
];

export const prepareSurveysTableHeaders = (
  labels,
  isListView = false,
  canViewAllSurveys = false
) => [
  {
    id: 1,
    title: labels.type,
    rowKey: 'type',
    sortAs: 'type',
    minWidth: 77,
    maxWidth: 77,
  },
  {
    id: 2,
    title: labels.title,
    rowKey: 'title',
    sortAs: 'title',
    isHighlighted: true,
    minWidth: 140,
    maxWidth: '1.5fr',
  },
  ...(isListView && canViewAllSurveys
    ? [
        {
          id: 3,
          title: labels.group,
          rowKey: 'group',
          sortAs: 'group',
          hasConditionalTooltip: true,
          maxWidth: '1fr',
        },
      ]
    : []),
  {
    id: 5,
    title: labels.sentAt,
    rowKey: 'sentAt',
    sortAs: 'sent_at',
    minWidth: 90,
    maxWidth: '1fr',
  },
  {
    id: 6,
    title: labels.expiry,
    rowKey: 'expireAt',
    sortAs: 'expire_at',
    minWidth: 90,
    maxWidth: '1fr',
  },
  {
    id: 7,
    title: labels.status,
    rowKey: 'status',
    isSortingDisabled: true,
    rowCellClass: 'status-cell',
    minWidth: 160,
    maxWidth: '1fr',
  },
];

export const prepareSurveysTableData = (
  surveys,
  translations,
  surveyTypes,
  surveyGroups,
  canViewAllSurveys,
  canManageAllSurveys,
  canViewSurveysCreated,
  canManageSurveysCreated,
  renderTitle,
  renderType,
  renderStatusAndCompleted,
  renderActions
) => {
  const translatedStatuses = prepareSurveyStatuses(translations.statuses);

  return surveys.map(survey => {
    const settingsUrl = `/surveys/${survey.id}/settings`;
    const type = getItemById(surveyTypes, survey.type);
    const status = getItemById(translatedStatuses, survey.status);
    const surveyGroup = surveyGroups.find(group => group.id === survey.group);
    const isDraft = status.id === SURVEY_STATUS_TYPES.DRAFT;

    return {
      id: survey.id,
      creatorId: survey.creator,
      title: renderTitle(survey),
      type: renderType(type),
      sentAt: formatDate(survey.sent_at),
      expireAt: formatDate(survey.expire_at),
      status: renderStatusAndCompleted(survey, status, type, isDraft),
      actions: renderActions(
        survey,
        canManageAllSurveys,
        canManageSurveysCreated
      ),
      group: surveyGroup?.title || '',
      isDraft,
      settingsUrl,
      ...(canViewAllSurveys || canViewSurveysCreated
        ? {
            cellLinks: {
              type: {
                to: settingsUrl,
              },
              title: {
                to: settingsUrl,
              },
              group: {
                to: settingsUrl,
              },
              sentAt: {
                to: settingsUrl,
              },
              expireAt: {
                to: settingsUrl,
              },
              status: {
                to: settingsUrl,
              },
            },
          }
        : {}),
    };
  });
};

export const prepareSurveyGroupsData = (
  surveyGroups,
  translations,
  surveyTypes,
  allSurveyGroups,
  canViewAllSurveys,
  canManageAllSurveys,
  canViewSurveysCreated,
  canManageSurveysCreated,
  renderTitle,
  renderType,
  renderStatusAndCompleted,
  renderActions
) => {
  return surveyGroups.map(group => ({
    ...group,
    surveys: !isArrayEmpty(group.surveys)
      ? prepareSurveysTableData(
          group.surveys,
          translations,
          surveyTypes,
          allSurveyGroups,
          canViewAllSurveys,
          canManageAllSurveys,
          canViewSurveysCreated,
          canManageSurveysCreated,
          renderTitle,
          renderType,
          renderStatusAndCompleted,
          renderActions
        )
      : [],
  }));
};

export const prepareCreateSurveyInitialData = (
  translations,
  type,
  surveyType,
  organizationUser,
  companyName,
  surveyGroup,
  initialData = {}
) => {
  const {
    TITLE,
    ATTRIBUTES,
    CREATED_FOR,
    REVIEWERS,
    SURVEY_TYPE,
    IS_ANONYMOUS,
    IS_STATISTICAL,
    FILL_INLINE,
    SCHEDULED_FOR,
    IS_SCHEDULE,
    EXPIRATION_DATE,
    HAS_AUTO_SHARE,
    SHARE_TO,
    DESCRIPTION,
    HAS_EMAIL_MESSAGE,
    EMAIL_MESSAGE,
    GROUP,
    HAS_AUTO_REMINDER,
    AUTO_REMINDERS,
    HAS_QUESTIONNAIRE,
    QUESTIONNAIRE,
  } = CREATE_SURVEY_FIELD_NAMES;
  const isEnpsType = [SURVEY_TYPES.ENPS, SURVEY_TYPES.NPS].includes(type);

  return {
    [TITLE]: isEnpsType
      ? surveyType?.name
      : prepareSurveyTitle(surveyType?.name, initialData?.[ATTRIBUTES]),
    [ATTRIBUTES]: initialData?.[ATTRIBUTES] ? initialData?.[ATTRIBUTES] : [],
    [CREATED_FOR]: [],
    [REVIEWERS]: initialData?.enpsReviewers ? initialData?.enpsReviewers : [],
    [SURVEY_TYPE]: type,
    [IS_ANONYMOUS]: initialData?.[IS_ANONYMOUS]
      ? initialData?.[IS_ANONYMOUS]
      : false,
    [IS_STATISTICAL]: true,
    [HAS_EMAIL_MESSAGE]: false,
    [HAS_AUTO_SHARE]: false,
    [HAS_QUESTIONNAIRE]: initialData?.[QUESTIONNAIRE] ?? false,
    [QUESTIONNAIRE]: EMPTY_QUESTIONNAIRE,
    [SHARE_TO]: [],
    [DESCRIPTION]: '',
    [EMAIL_MESSAGE]: translations.defaultEmailMessage,
    [FILL_INLINE]: false,
    [SCHEDULED_FOR]: addHoursToCurrentStartHour(),
    [EXPIRATION_DATE]: addDaysFromSpecificHour(),
    [IS_SCHEDULE]: false,
    [HAS_AUTO_REMINDER]: initialData?.[HAS_AUTO_REMINDER] ?? true,
    [AUTO_REMINDERS]: initialData?.[AUTO_REMINDERS] ?? [
      AUTO_REMINDER_OPTION_IDS.DAY_BEFORE_END,
    ],
    ...(surveyGroup ? { [GROUP]: surveyGroup.id } : {}),
    companyAvatar: organizationUser(),
    company: companyName,
    ...initialData,
  };
};

export const prepareCreateSurveyColumns = (
  labels,
  classes,
  defaultEmailMessage,
  surveyType,
  surveyTypes,
  predefinedData,
  onChangeSurveyType
) => {
  const selectedType = getItemById(surveyTypes, surveyType);
  const { SURVEY_TYPE, HAS_EMAIL_MESSAGE } = CREATE_SURVEY_FIELD_NAMES;

  return (
    selectedType?.columns?.map(column => {
      if (column.id === 1) {
        return {
          ...column,
          title: labels[column.translationKey],
          fields: column.fields.map(field => {
            if (field.name === SURVEY_TYPE) {
              return {
                ...field,
                menuClass: classes.selectMenu,
                menuListClass: classes.selectMenuList,
                isDisabled: !!predefinedData?.isDraftActivation,
                callbackFnc: onChangeSurveyType,
              };
            }
            if (field.name === HAS_EMAIL_MESSAGE) {
              return {
                ...field,
                dependants: [
                  {
                    name: CREATE_SURVEY_FIELD_NAMES.EMAIL_MESSAGE,
                    value: defaultEmailMessage,
                  },
                ],
              };
            }

            return field;
          }),
        };
      }
      return {
        ...column,
        fields: column.fields,
        title: labels[column.translationKey],
      };
    }) || []
  );
};

export const prepareSurveyDataForCreation = (
  surveyData,
  isGroupView,
  isDraftActivation,
  isDraftCreation,
  hasAutoShare
) => {
  const {
    TITLE,
    ATTRIBUTES,
    ATTRIBUTES_WITH_COMMENT,
    CREATED_FOR,
    REVIEWERS,
    SURVEY_TYPE,
    IS_ANONYMOUS,
    IS_STATISTICAL,
    FILL_INLINE,
    SCHEDULED_FOR,
    IS_SCHEDULE,
    CLIENT_URL,
    EXPIRATION_DATE,
    HAS_EMAIL_MESSAGE,
    EMAIL_MESSAGE,
    SHARE_TO,
    DESCRIPTION,
    SURVEY_SUMMARY,
    GROUP,
    STATUS,
    SEND,
    HAS_AUTO_REMINDER,
    AUTO_REMINDERS,
    HAS_QUESTIONNAIRE,
    QUESTIONNAIRE,
  } = CREATE_SURVEY_FIELD_NAMES;
  const { PEER, TEAM, DIRECT } = SURVEY_TYPES;
  const hasQuestionnaire = surveyData[HAS_QUESTIONNAIRE];
  const hasAttributes =
    isObject(surveyData[ATTRIBUTES]) && !isObjectEmpty(surveyData[ATTRIBUTES]);

  return {
    [TITLE]: surveyData[TITLE],
    ...(surveyData[HAS_EMAIL_MESSAGE]
      ? { [EMAIL_MESSAGE]: surveyData[EMAIL_MESSAGE] }
      : {}),
    [IS_ANONYMOUS]: surveyData[IS_ANONYMOUS],
    [IS_STATISTICAL]: surveyData[IS_STATISTICAL],
    [CREATED_FOR]: [DIRECT, PEER].includes(surveyData[SURVEY_TYPE])
      ? surveyData[CREATED_FOR][0].id
      : getObjectToNumberArray(surveyData[CREATED_FOR]),
    [REVIEWERS]: [DIRECT, TEAM].includes(surveyData[SURVEY_TYPE])
      ? surveyData[REVIEWERS][0].id
      : getObjectToNumberArray(surveyData[REVIEWERS]),
    ...(hasQuestionnaire
      ? {
          [QUESTIONNAIRE]: {
            name: surveyData[QUESTIONNAIRE]?.name ?? '',
            description: surveyData[QUESTIONNAIRE]?.description ?? '',
            questions: (surveyData[QUESTIONNAIRE]?.questions ?? []).map(
              (question, index) => ({
                text: question.text,
                type: question.type,
                order_id: index + 1,
                options:
                  question.type === QUESTIONNAIRE_QUESTION_TYPES.OPEN_TEXT
                    ? []
                    : (question.options || []).map((option, optionIndex) => ({
                        text: option.text,
                        order: option.order ?? optionIndex + 1,
                      })),
              })
            ),
          },
        }
      : { [QUESTIONNAIRE]: null }),
    ...(!hasAttributes
      ? {
          [ATTRIBUTES]: getObjectToNumberArray(surveyData[ATTRIBUTES]),
          [ATTRIBUTES_WITH_COMMENT]: surveyData[ATTRIBUTES]?.reduce(
            (acc, curr) => ({
              ...acc,
              [curr.id]: curr.has_answer_comment,
            }),
            {}
          ),
        }
      : { [ATTRIBUTES]: [], [ATTRIBUTES_WITH_COMMENT]: {} }),
    [SCHEDULED_FOR]: surveyData[IS_SCHEDULE] ? surveyData[SCHEDULED_FOR] : null,
    ...(hasAutoShare
      ? {
          [SURVEY_SUMMARY]: {
            [TITLE]: `${surveyData[TITLE]}-Summary`,
            ...(isEmpty(surveyData[DESCRIPTION])
              ? {
                  description: surveyData[DESCRIPTION],
                }
              : {}),
            [SHARE_TO]: getObjectToNumberArray(surveyData[SHARE_TO]),
          },
        }
      : {}),
    [CLIENT_URL]: `${window.location.origin}/fill-survey`,
    [FILL_INLINE]: !isDraftCreation ? surveyData[FILL_INLINE] : false,
    [EXPIRATION_DATE]: !isDraftCreation ? surveyData[EXPIRATION_DATE] : null,
    ...(surveyData[HAS_AUTO_REMINDER] && !isDraftCreation
      ? {
          [AUTO_REMINDERS]: pruneAutoReminders(
            surveyData[AUTO_REMINDERS],
            getCreateSurveyAutoReminderStartDate(surveyData),
            surveyData[EXPIRATION_DATE]
          ),
        }
      : {}),
    ...(isDraftCreation
      ? { [STATUS]: SURVEY_STATUS_TYPES.DRAFT, [SEND]: false }
      : {}),
    ...(isGroupView || isDraftActivation
      ? { ...(surveyData[GROUP] ? { [GROUP]: surveyData[GROUP] } : {}) }
      : {}),
  };
};

export const prepareNoSurveyResultsData = (
  translations,
  isLoading,
  isGroupView,
  hasFilters
) => {
  if (isLoading) {
    return {
      title: '',
      content: '',
    };
  }

  if (isGroupView) {
    return {
      title: hasFilters ? '' : translations.noGroupResultsTitle,
      content: hasFilters
        ? translations.noSearchResultsMessage
        : translations.noGroupResultsMessage,
    };
  }

  return {
    title: hasFilters ? '' : translations.noResultsTitle,
    content: hasFilters
      ? translations.noSearchResultsMessage
      : translations.noResultsMessage,
  };
};

export const prepareSurveyDeleteTranslations = (translations, survey) => {
  const isScheduled =
    survey?.scheduled_for && isDateInFuture(survey.scheduled_for);
  const isDraft = survey?.status === SURVEY_STATUS_TYPES.DRAFT;

  if (!survey) return {};

  if (isScheduled) {
    return translations.scheduledSurveyDeleteDialog;
  }

  if (isDraft) {
    return translations.draftSurveyDeleteDialog;
  }

  return translations.surveyDeleteDialog;
};

export const hasReviewCommentsCheck = (currentReviewId, reviews) => {
  const review = getItemById(reviews, currentReviewId);

  return review?.has_text_reply_results && !review?.text_reply_questions;
};

export const prepareReviewComments = (
  neutralAnswerLabel,
  reviewComments,
  survey,
  review
) => {
  const { isAnonymous, type, reviewers } = survey;
  const { questions, hasNeutralAnswer } = review;

  const [reviewer] = reviewers;
  const isTeamReview = type === SURVEY_TYPES.TEAM;

  const initialReviewCommentators =
    isTeamReview && !isAnonymous ? [{ ...reviewer }] : [];

  const allComments = Object.entries(reviewComments).reduce(
    (acc, [questionKey, questionComments]) => {
      const question = getItemById(questions, +questionKey);
      const answers = question.answers.reduce(
        (answersAcc, currentAnswer, currentIndex) => {
          const replies = questionComments.filter(
            currentReply =>
              currentReply.reply && currentReply.answer === currentAnswer.id
          );

          if (!isArrayEmpty(replies)) {
            return [
              ...answersAcc,
              {
                ...(hasNeutralAnswer &&
                currentIndex === question.answers.length - 1
                  ? { ...currentAnswer, text: neutralAnswerLabel }
                  : currentAnswer),
                replies,
              },
            ];
          }
          return answersAcc;
        },
        []
      );

      const uniqueReviewCommentators =
        isAnonymous || isTeamReview
          ? []
          : questionComments.reduce((commentsAcc, currentComment) => {
              const isReviewerInList = isItemInList(
                acc.reviewCommentators,
                currentComment.reviewer
              );

              if (!isReviewerInList) {
                return [...commentsAcc, { ...currentComment.reviewer }];
              }
              return commentsAcc;
            }, []);

      const comments = {
        id: +questionKey,
        questionText: question.text,
        answers,
      };
      return {
        ...acc,
        reviewCommentators: [
          ...acc.reviewCommentators,
          ...uniqueReviewCommentators,
        ],
        allComments: [...acc.allComments, { ...comments }],
      };
    },
    {
      reviewCommentators: initialReviewCommentators,
      allComments: [],
    }
  );

  return allComments;
};

export const prepareReviewCommentsBySelectedView = (
  reviewComments,
  reviewCommentsByQuestion,
  commentator,
  questionId,
  isViewAll,
  isTeamReview
) => {
  if (isViewAll || (isTeamReview && !questionId)) {
    return reviewComments;
  }

  if (!isObjectEmpty(commentator)) {
    return reviewComments.reduce((acc, questionComments) => {
      const answers = questionComments.answers.reduce((answerAcc, answer) => {
        const replies = answer.replies.filter(
          reply => reply.reviewer.id === commentator.id
        );

        if (!isArrayEmpty(replies)) {
          return [...answerAcc, { ...answer, replies }];
        }

        return answerAcc;
      }, []);

      if (!isArrayEmpty(answers)) {
        return [...acc, { ...questionComments, answers }];
      }

      return acc;
    }, []);
  }

  if (questionId) {
    return reviewCommentsByQuestion.filter(
      questionComments => questionComments.id === questionId
    );
  }

  return reviewComments;
};

export const prepareCommentsByQuestion = (
  reviewComments,
  selectedUsers = []
) => {
  if (isArrayEmpty(reviewComments)) return [];
  if (isArrayEmpty(selectedUsers)) return reviewComments;

  return reviewComments.reduce((acc, questionComments) => {
    const answers = questionComments.answers.reduce((answersAcc, answer) => {
      const replies = answer.replies.filter(reply =>
        isValueInList(selectedUsers, reply.reviewer.id)
      );

      if (!isArrayEmpty(replies)) {
        return [...answersAcc, { ...answer, replies }];
      }

      return answersAcc;
    }, []);

    if (!isArrayEmpty(answers)) {
      return [...acc, { ...questionComments, answers }];
    }

    return acc;
  }, []);
};

export const getCustomSurveyPseudoAttribute = (overrides = {}) => ({
  with_zero_weight_answer: false,
  with_neutral_answer: false,
  answer_count: QUESTIONNAIRE_QUESTION_DEFAULT_OPTIONS_COUNT,
  text_reply_questions: false,
  layout: ATTRIBUTE_CONSTANTS.verticalLayout,
  is_editable: true,
  id: 'custom_questionnaire',
  ...overrides,
});

export const createQuestionnaireQuestion = (
  questionTitle,
  type,
  order,
  optionsCount
) => {
  const isOpenEndedQuestion = type === QUESTIONNAIRE_QUESTION_TYPES.OPEN_TEXT;
  const options = !isOpenEndedQuestion
    ? [...Array(optionsCount).keys()].map((_, i) => {
        return {
          id: nanoid(),
          order: i + 1,
          text: (i + 1).toString(),
        };
      })
    : [];

  return {
    id: nanoid(),
    text: questionTitle,
    order,
    type,
    isQuestionnaire: true,
    optionsCount: isOpenEndedQuestion ? 0 : optionsCount,
    options: optionsCount !== 0 ? options : [],
  };
};

export const duplicateQuestionnaireQuestion = (question, order) => {
  const isOpenEnded = question.type === QUESTIONNAIRE_QUESTION_TYPES.OPEN_TEXT;
  const optionsCount = isOpenEnded
    ? 0
    : question.options?.length || QUESTIONNAIRE_QUESTION_DEFAULT_OPTIONS_COUNT;

  return {
    ...question,
    id: nanoid(),
    order,
    optionsCount,
    options: isOpenEnded
      ? []
      : (question.options || []).map(option => ({
          ...option,
          id: nanoid(),
        })),
  };
};

export const addNewQuestionnaireQuestionOption = (question, newCount) => {
  const isOpenEnded = question.type === QUESTIONNAIRE_QUESTION_TYPES.OPEN_TEXT;

  if (isOpenEnded || newCount === question.options?.length) {
    return question;
  }

  const templateQuestion = createQuestionnaireQuestion(
    question.text,
    question.type,
    question.order ?? question.order_id,
    newCount
  );

  const options = templateQuestion.options.map((option, index) => ({
    ...option,
    id: question.options?.[index]?.id ?? option.id,
    text: question.options?.[index]?.text ?? option.text,
  }));

  return {
    ...question,
    optionsCount: newCount,
    options,
  };
};

export const prepareQuestionnaireQuestionOptions = translations =>
  QUESTIONNAIRE_QUESTION_OPTIONS.map(option => ({
    ...option,
    label: translations[option.translationKey],
  }));

export const CUSTOM_QUESTIONNAIRE_ATTRIBUTE_ID = 'custom_questionnaire';

export const getQuestionnaireQuestions = questionnaire =>
  questionnaire?.questions ?? [];

export const normalizeQuestionnaireQuestionPreview = (question, index) => {
  const isOpenEnded = question.type === QUESTIONNAIRE_QUESTION_TYPES.OPEN_TEXT;

  return {
    ...question,
    id: question.id || index + 1,
    order_id: question.order ?? question.order_id ?? index + 1,
    text_reply_questions: isOpenEnded,
    answer_count: isOpenEnded ? 0 : question.options?.length || 0,
    answers: isOpenEnded
      ? []
      : (question.options || []).map((option, optionIndex) => ({
          ...option,
          id: option.id ?? option.order ?? optionIndex + 1,
        })),
  };
};

export const prepareQuestionnairePreviewData = questionnare => {
  const description = questionnare?.description || '';

  return {
    id: questionnare.id || -1,
    name: questionnare?.name || '',
    description: isRichTextEditorEmpty(description) ? description : '',
    questions: (questionnare?.questions ?? []).map(
      normalizeQuestionnaireQuestionPreview
    ),
    layout: ATTRIBUTE_CONSTANTS.verticalLayout,
    [ATTRIBUTE_CONSTANTS.fields.withTextReplyQuestions]: false,
    [ATTRIBUTE_CONSTANTS.fields.withNeutralAnswer]: false,
    [ATTRIBUTE_CONSTANTS.fields.withZeroValueAnswer]: false,
    [ATTRIBUTE_CONSTANTS.fields.withAdditionalFeedback]: false,
    question_count: questionnare?.questions?.length ?? 0,
  };
};

export const prepareAnswerFields = isVertical => {
  return {
    required: true,
    validators: [
      { type: 'isEmpty', validator: isEmpty },
      {
        type: 'maxAnswerEntryLength',
        validator: value => validateLength(value, 0, 2000),
      },
      ...(isVertical
        ? []
        : [{ type: 'forbiddenCharacters', validator: forbiddenCharacters }]),
    ],
  };
};

export const validateQestionnaireQuestions = (questionnaire, values) => {
  const { HAS_QUESTIONNAIRE } = CREATE_SURVEY_FIELD_NAMES;

  if (!values?.[HAS_QUESTIONNAIRE]) {
    return false;
  }

  let errors = {};
  const name = questionnaire?.name ?? '';
  const nameError = QUESTIONNAIRE_NAME_VALIDATORS.find(
    validator => !validator.validator(name)
  );

  if (nameError) {
    errors = { ...errors, name: nameError.type };
  }

  const description = questionnaire?.description ?? '';
  const descriptionError = QUESTIONNAIRE_DESCRIPTION_VALIDATORS.find(
    validator => !validator.validator(description)
  );

  if (descriptionError) {
    errors = { ...errors, description: descriptionError.type };
  }

  const questions = questionnaire?.questions ?? [];

  if (isArrayEmpty(questions)) {
    errors = { ...errors, questions: 'required' };
  }

  const questionErrors = questions.reduce((acc, question, index) => {
    const titleError = TITLE_FIELD.validators.find(
      validator => !validator.validator(question.text)
    );
    const hasTitleError = Boolean(titleError);
    let currentQuestionErrors = {};

    if (hasTitleError) {
      currentQuestionErrors = {
        ...currentQuestionErrors,
        error_question_title: titleError.type,
      };
    }

    if (question.type !== QUESTIONNAIRE_QUESTION_TYPES.OPEN_TEXT) {
      const optionsCount = question.options?.length || 0;

      if (
        optionsCount < QUESTIONNAIRE_QUESTION_MIN_OPTIONS ||
        optionsCount > QUESTIONNAIRE_QUESTION_MAX_OPTIONS
      ) {
        currentQuestionErrors = {
          ...currentQuestionErrors,
          error_answer_count: 'invalidAnswerCount',
        };
      } else {
        const optionErrors = (question.options || []).reduce(
          (accum, current, optionIndex) => {
            const optionError = prepareAnswerFields(true).validators.find(
              validator => !validator.validator(current.text)
            );

            return {
              ...accum,
              ...(optionError
                ? {
                    [`error_answer_title_${optionIndex}`]: optionError.type,
                  }
                : {}),
            };
          },
          {}
        );

        if (Object.keys(optionErrors).length) {
          currentQuestionErrors = {
            ...currentQuestionErrors,
            ...optionErrors,
          };
        }
      }
    }

    return Object.keys(currentQuestionErrors).length
      ? { ...acc, [index]: currentQuestionErrors }
      : acc;
  }, {});

  if (Object.keys(questionErrors).length) {
    errors = { ...errors, questions: questionErrors };
  }

  return Object.keys(errors).length ? errors : false;
};
